
import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { signIn } from '../utils/actions/authActions';
import styles from './LoginPage.module.css';
import UserContext from '../context/UserContext';
import logo from '../assets/Images/logo.png';
import { FiUser, FiPhone, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, signInWithPhoneNumber, RecaptchaVerifier } from 'firebase/auth';
// Country codes data
const COUNTRY_CODES = [
{ code: '+91', flag: '🇮🇳', name: 'India' },
{ code: '+1', flag: '🇺🇸', name: 'United States' },
{ code: '+44', flag: '🇬🇧', name: 'United Kingdom' },
{ code: '+61', flag: '🇦🇺', name: 'Australia' },
{ code: '+86', flag: '🇨🇳', name: 'China' },
{ code: '+33', flag: '🇫🇷', name: 'France' },
{ code: '+49', flag: '🇩🇪', name: 'Germany' },
{ code: '+81', flag: '🇯🇵', name: 'Japan' },
{ code: '+52', flag: '🇲🇽', name: 'Mexico' },
{ code: '+55', flag: '🇧🇷', name: 'Brazil' },
];
const LoginPage = () => {
const { userData, setUserData } = useContext(UserContext);
// State for authentication method tabs
const [activeTab, setActiveTab] = useState('email');
// Email login state
const [email, setEmail] = useState('');
const [password, setPassword] = useState('');
// Phone login state
const [phoneNumber, setPhoneNumber] = useState('');
const [phonePassword, setPhonePassword] = useState('');
const [selectedCountry, setSelectedCountry] = useState(COUNTRY_CODES[0]); // Default to India
const [showCountryDropdown, setShowCountryDropdown] = useState(false);
// UI state
const [error, setError] = useState(null);
const [isLoading, setIsLoading] = useState(false);
const [redirectURL, setRedirectURL] = useState('');
const [showPassword, setShowPassword] = useState(false);
// Refs
const countryDropdownRef = useRef(null);
const phoneInputRef = useRef(null);
const navigate = useNavigate();
const location = useLocation();
// Close country dropdown when clicking outside
useEffect(() => {
const handleClickOutside = (event) => {
if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
setShowCountryDropdown(false);
}
};
document.addEventListener('mousedown', handleClickOutside);
return () => {
  document.removeEventListener('mousedown', handleClickOutside);
};
}, []);
// Check for a saved redirect URL when component mounts
useEffect(() => {
const savedRedirect = sessionStorage.getItem('redirectAfterLogin');
if (savedRedirect) {
setRedirectURL(savedRedirect);
console.log('Found saved redirect:', savedRedirect);
}
}, []);
useEffect(() => {
// Check for a redirect URL in the query parameters
const params = new URLSearchParams(window.location.search);
const redirectTo = params.get('redirectTo');
if (redirectTo) {
  sessionStorage.setItem('redirectAfterLogin', redirectTo);
  setRedirectURL(redirectTo);
  console.log('Found redirect URL in query params:', redirectTo);
}
}, []);
// Get the redirect path from location state or saved redirect, with fallback
const getRedirectPath = (userData) => {
// Priority 1: Saved redirect from session storage
if (redirectURL) {
return redirectURL;
}
// Priority 2: From location state (react router)
if (location.state?.from) {
  return location.state.from;
}

// Priority 3: User profile as default
return `/${userData?.userName || ''}`;
};
// Input handlers
const handleEmailChange = (e) => setEmail(e.target.value);
const handlePasswordChange = (e) => setPassword(e.target.value);
const handlePhoneChange = (e) => {
const value = e.target.value;
// Only allow numbers
if (value && !/^\d+$/.test(value)) return;

setPhoneNumber(value);
};
const handlePhonePasswordChange = (e) => setPhonePassword(e.target.value);
const selectCountry = (country) => {
setSelectedCountry(country);
setShowCountryDropdown(false);
phoneInputRef.current?.focus();
};
const handleLogin = async (e) => {
e.preventDefault();
setIsLoading(true);
setError(null);
try {
  // Email login
  if (activeTab === 'email') {
    if (!email || !password) {
      setError('Please enter both email and password');
      setIsLoading(false);
      return;
    }
  
    const result = await signIn(email, password);
    if (result?.userData) {
      // Get the appropriate redirect path
      let redirectPath = getRedirectPath(result.userData);
      
      // Clear the saved redirect
      sessionStorage.removeItem('redirectAfterLogin');
      
      console.log('Redirecting to:', redirectPath);
      
      await new Promise(resolve => {
        setTimeout(() => {
          navigate(redirectPath);
          resolve();
        }, 2000);
      });
    }
  }
  // Phone login
  else {
    if (!phoneNumber || !phonePassword) {
      setError('Please enter both phone number and password');
      setIsLoading(false);
      return;
    }
    
    // Format phone number for the API
    const formattedNumber = `${selectedCountry.code}${phoneNumber}`;
    
    // Here we'll use email-password authentication but with the phone as the "email"
    // For a production app, you may want to implement actual phone auth with Firebase
    // This is just to match the existing auth system for now
    const phoneAsEmail = `${formattedNumber.replace('+', '')}@mail.trillio.in`;
    
    const result = await signIn(phoneAsEmail, phonePassword);
    if (result?.userData) {
      // Get the appropriate redirect path
      let redirectPath = getRedirectPath(result.userData);
      
      // Clear the saved redirect
      sessionStorage.removeItem('redirectAfterLogin');
      
      console.log('Redirecting to:', redirectPath);
      
      await new Promise(resolve => {
        setTimeout(() => {
          navigate(redirectPath);
          resolve();
        }, 2000);
      });
    }
  }
} catch (error) {
  console.error(error);
  
  if (activeTab === 'email') {
    setError('Invalid email or password. Please try again.');
  } else {
    setError('Invalid phone number or password. Please try again.');
  }
  
  setIsLoading(false);
}
};
return (
<div className={styles['login-page']}>
<div className={styles['login-container']}>
<div className={styles['login-left']}>
<div className={styles['logo-section']}>
<img src={logo} alt="Trillio Logo" className={styles.logo} />
</div>
<h2 className={styles['welcome-text']}>Welcome Back!</h2>
<p className={styles['login-description']}>
Log in to access your tour management dashboard. Keep track of your schedules,
manage your team, and stay organized with Trillio's comprehensive tools.
</p>
{redirectURL && (
<div className={styles['redirect-notice']}>
<p>You'll be redirected back after login.</p>
</div>
)}
</div>
    <div className={styles['login-right']}>
      <div className={styles['login-form-container']}>
        <h2>Sign In</h2>
        
        {/* Login Tabs */}
        <div className={styles['login-tabs']}>
          <button 
            className={`${styles['tab-button']} ${activeTab === 'email' ? styles['active-tab'] : ''}`}
            onClick={() => {
              setActiveTab('email');
              setError(null);
            }}
          >
            <FiUser /> Email
          </button>
          <button 
            className={`${styles['tab-button']} ${activeTab === 'phone' ? styles['active-tab'] : ''}`}
            onClick={() => {
              setActiveTab('phone');
              setError(null);
            }}
          >
            <FiPhone /> Phone
          </button>
        </div>
        
        {error && <div className={styles.error}>{error}</div>}
        
        <form onSubmit={handleLogin}>
          {/* Email Tab */}
          {activeTab === 'email' && (
            <>
              <div className={styles['form-group']}>
                <label htmlFor="email">Email Address</label>
                <div className={styles['input-icon-wrapper']}>
                  <FiUser className={styles['input-icon']} />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="Enter your email"
                    required
                    className={styles['input-with-icon']}
                  />
                </div>
              </div>
              <div className={styles['form-group']}>
                <label htmlFor="password">Password</label>
                <div className={styles['input-icon-wrapper']}>
                  <FiLock className={styles['input-icon']} />
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={handlePasswordChange}
                    placeholder="Enter your password"
                    required
                    className={styles['input-with-icon']}
                  />
                  <button
                    type="button"
                    className={styles['password-toggle']}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
            </>
          )}
          
          {/* Phone Tab */}
          {activeTab === 'phone' && (
            <>
              <div className={styles['form-group']}>
                <label htmlFor="phone">Phone Number</label>
                <div className={styles['phone-input-container']}>
                  {/* Country code dropdown */}
                  <div className={styles['country-dropdown']} ref={countryDropdownRef}>
                    <button 
                      type="button"
                      className={styles['country-button']}
                      onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                    >
                      <span className={styles['country-flag']}>{selectedCountry.flag}</span>
                      <span className={styles['country-code']}>{selectedCountry.code}</span>
                      <span className={styles['dropdown-arrow']}>▼</span>
                    </button>
                    
                    {showCountryDropdown && (
                      <div className={styles['dropdown-menu']}>
                        <div className={styles['dropdown-search']}>
                          <input 
                            type="text" 
                            placeholder="Search countries..."
                            className={styles['dropdown-search-input']}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className={styles['dropdown-list']}>
                          {COUNTRY_CODES.map((country, index) => (
                            <div 
                              key={index} 
                              className={styles['dropdown-item']}
                              onClick={() => selectCountry(country)}
                            >
                              <span className={styles['country-flag']}>{country.flag}</span>
                              <span className={styles['country-code']}>{country.code}</span>
                              <span className={styles['country-name']}>{country.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Phone number input */}
                  <input
                    id="phone"
                    ref={phoneInputRef}
                    type="tel"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    className={styles['phone-input']}
                    placeholder="Enter phone number"
                    required
                  />
                </div>
              </div>
              <div className={styles['form-group']}>
                <label htmlFor="phonePassword">Password</label>
                <div className={styles['input-icon-wrapper']}>
                  <FiLock className={styles['input-icon']} />
                  <input
                    id="phonePassword"
                    type={showPassword ? "text" : "password"}
                    value={phonePassword}
                    onChange={handlePhonePasswordChange}
                    placeholder="Enter your password"
                    required
                    className={styles['input-with-icon']}
                  />
                  <button
                    type="button"
                    className={styles['password-toggle']}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
            </>
          )}
          
          <div className={styles['forgot-password']}>
            <Link to="/forgot-password">Forgot Password?</Link>
          </div>
          
          <button 
            type="submit" 
            disabled={isLoading} 
            className={isLoading ? styles.loadingButton : ''}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        
        <div className={styles['signup-link']}>
          New to Trillio?
          <Link to="/signup">Create an account</Link>
        </div>
      </div>
    </div>
  </div>
</div>
);
};
export default LoginPage;
